import * as React from 'react';
import * as MuiCore from '@mui/material';
import { useChildProfileDetails } from '../../../data/DataProviders/ChildProfileDetailsProvider';
import { useSelectedOrganizationData, isRecordFound } from '../../../hooks/data-hooks';
import { isDataLoaded } from '../../../types';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material';
import { Icon } from '../../../lib/icons';
import { DataIndicators } from '../../DataIndicators';

type Props<TValue, TOptions> = {
    groups: Array<{
        key: string;
        profileId?: string;
        profileName?: string;
        items?: TValue[];
    }>;
    component: React.FunctionComponent<{
        testId?: string;
        value: TValue;
        isLast: boolean;
        options?: TOptions;
        onClick?: () => void;
    }>;
    componentTestId?: string;
    options?: TOptions;
    navigationFn?: (arg: TValue) => void;
};

const useStyles = makeStyles((theme: Theme) => ({
    expand: {
        transform: 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
}));

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function makeDeviceTypeListGroupsComponent<TValue, TOptions = never>() {
    const component: React.FC<Props<TValue, TOptions>> = (props) => {
        const ListItemComponent = props.component;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const childProfileDetails = useChildProfileDetails();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const selectedOrganization = useSelectedOrganizationData();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const orgId = isRecordFound(selectedOrganization) ? selectedOrganization.value.id : '';
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const classes = useStyles();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [open, setOpen] = React.useState(null);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [id, setId] = React.useState('');
        // eslint-disable-next-line react-hooks/rules-of-hooks
        React.useEffect(() => {
            if (id !== '') {
                childProfileDetails.getChildProfileData(orgId, id);
            }
        }, [id]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const handleOpen = (profileId: any): void => {
            setId(profileId || '');
            if (open === profileId) {
                setOpen(null);
            } else {
                setOpen(profileId);
            }
        };
        return (
            <div>
                {props.groups.map((group, groupIndex) => (
                    <div data-testid="list-group" key={groupIndex}>
                        <MuiCore.Card style={{ textDecorationSkipInk: 'inherit' }}>
                            <MuiCore.List
                                component="div"
                                disablePadding
                                subheader={
                                    group.key ? (
                                        <MuiCore.ListSubheader
                                            onClick={(): void => {
                                                handleOpen(group.profileId);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <MuiCore.ListItemText
                                                data-testid="list-group-title"
                                                primary={`${group.key}`}
                                                primaryTypographyProps={{
                                                    variant: 'subtitle2',
                                                    color: 'primary',
                                                    style: { fontWeight: 600, lineHeight: 3 },
                                                }}
                                            />

                                            <MuiCore.ListItemSecondaryAction>
                                                {group.profileId !== '' && (
                                                    <MuiCore.IconButton>
                                                        <Icon.ExpandMore
                                                            className={`${classes.expand} ${
                                                                open === group.profileId ? classes.expandOpen : ''
                                                            }`}
                                                        />
                                                    </MuiCore.IconButton>
                                                )}
                                            </MuiCore.ListItemSecondaryAction>
                                        </MuiCore.ListSubheader>
                                    ) : (
                                        undefined
                                    )
                                }
                            >
                                <MuiCore.Divider />
                                <MuiCore.Collapse in={open === group.profileId} timeout="auto" unmountOnExit>
                                    {!isDataLoaded(childProfileDetails.profiles) && (
                                        <DataIndicators
                                            errorMessage="Child  Profiles  not Found"
                                            emptyMessage="Child  Profiles"
                                            inline
                                            data={childProfileDetails.profiles}
                                            emptyIcon="Devices"
                                            isEmpty={false}
                                            isDetailedInfo={false}
                                        />
                                    )}
                                    {isDataLoaded(childProfileDetails.profiles) &&
                                        id !== '' &&
                                        open === group.profileId && (
                                            <MuiCore.List component="div" disablePadding>
                                                {childProfileDetails.profiles.value.childProfiles.map(
                                                    (item: any, listItemIndex: number) => (
                                                        <div key={listItemIndex}>
                                                            <ListItemComponent
                                                                key={listItemIndex}
                                                                testId={props.componentTestId}
                                                                value={item}
                                                                isLast={listItemIndex + 1 === group.items?.length}
                                                                options={props.options}
                                                                onClick={(): void =>
                                                                    props.navigationFn && props.navigationFn(item)
                                                                }
                                                            ></ListItemComponent>
                                                        </div>
                                                    )
                                                )}
                                            </MuiCore.List>
                                        )}
                                </MuiCore.Collapse>
                            </MuiCore.List>
                        </MuiCore.Card>
                    </div>
                ))}
            </div>
        );
    };

    return component;
}
